/**
 * Application Configuration
 *
 * Centralized configuration management for different environments.
 * Supports: dev, qa, stage, and prod environments.
 */

type Environment = 'dev' | 'qa' | 'stage' | 'prod';

interface AppConfig {
  apiUrl: string;
  gtmId: string;
  environment: Environment;
  isDevelopment: boolean;
  isProduction: boolean;
}

/**
 * Environment-specific configurations.
 * Prefer REACT_APP_API_URL / REACT_APP_GTM_ID env vars (set in CI/CD).
 * Fallback map used only when env vars are not set (local dev).
 */
const ENV_FALLBACKS: Record<Environment, Omit<AppConfig, 'environment' | 'isDevelopment' | 'isProduction'>> = {
  dev: {
    apiUrl: 'http://localhost:3001/api',
    gtmId: '',
  },
  qa: {
    apiUrl: process.env.REACT_APP_API_URL || 'http://localhost:3001/api',
    gtmId: process.env.REACT_APP_GTM_ID || '',
  },
  stage: {
    apiUrl: process.env.REACT_APP_API_URL || 'http://localhost:3001/api',
    gtmId: process.env.REACT_APP_GTM_ID || '',
  },
  prod: {
    apiUrl: process.env.REACT_APP_API_URL || 'http://localhost:3001/api',
    gtmId: process.env.REACT_APP_GTM_ID || '',
  },
};

/**
 * Determines the current environment from REACT_APP_ENV variable
 * Defaults to 'prod' if not set or invalid
 */
function getEnvironment(): Environment {
  const env = process.env.REACT_APP_ENV as Environment;
  return env && ['dev', 'qa', 'stage', 'prod'].includes(env) ? env : 'prod';
}

/**
 * Creates the application configuration object
 */
function createConfig(): AppConfig {
  const environment = getEnvironment();
  const envConfig = ENV_FALLBACKS[environment];

  return {
    ...envConfig,
    environment,
    isDevelopment: environment === 'dev',
    isProduction: environment === 'prod',
  };
}

/**
 * Main configuration object - use this throughout the application
 *
 * Example usage:
 * ```typescript
 * import { config } from './config';
 *
 * const response = await fetch(`${config.apiUrl}/users`);
 * ```
 */
export const config = createConfig();

/**
 * Legacy helper function for backwards compatibility
 * @deprecated Use config.apiUrl instead
 */
export const getApiUrl = (): string => config.apiUrl;

/**
 * Helper function to get GTM ID
 * @deprecated Use config.gtmId instead
 */
export const getGtmId = (): string => config.gtmId;
